/**
 * buildWorkspacePackage - PIPH-89 Workspace packaging for Piph Author LLM
 *
 * Assembles the user message sent to Claude's API for alt text generation.
 * Wraps the output of `buildStructuredAltText()` into the format the Piph
 * Author LLM prompt expects.
 *
 * @package @alchemiesolns/svg-interactive
 * @see PIPH-89
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import type { WorkspaceFeatureResult } from '@alchemiesolns/feature-extractor-compat';
import { findConnectedComponents } from './graphUtils';
import { buildStructuredAltText } from './buildStructuredAltText';
import { buildStereoSpatialBlock } from './stereoSpatialBlock';
import { buildCurvedArrowBlock } from './curvedArrowBlock';
import { buildWorkspaceSummary } from './workspaceSummaryTemplate';

/**
 * Workspace specification - structured molecular workspace or author-entered text.
 *
 * Mirrors ProductSpec/ReactantSpec from buildReactionSchemePackage. When the
 * author replaces a structure with free-form text via the "T" button, the text
 * variant ensures the LLM payload reflects that choice instead of an empty workspace.
 */
export type WorkspaceSpec =
  | { type: 'workspace'; workspace: MolecularWorkspace; featureResult: WorkspaceFeatureResult }
  | { type: 'rawText'; text: string };

/**
 * Options for buildWorkspacePackage
 */
export interface WorkspacePackageOptions {
  /**
   * Context add-ons to append after the molecule block.
   * Reserved for future implementation (separate Jira cards).
   */
  addOns?: Array<{ header: string; content: string }>;

  /**
   * PIPH-97: When true, append stereo spatial context block describing
   * the 3D arrangement of bonds around stereocenters.
   */
  stereoSpatial?: boolean;

  /**
   * PIPH-98: When true, append curved arrow context block describing
   * electron movement arrows for accessibility.
   */
  curvedArrows?: boolean;
}

/**
 * Internal representation of a molecule during processing
 */
interface MoleculeData {
  atomCount: number;
  altText: string;
  isSimple: boolean;
  originalIndex: number;
}

/**
 * Check if alt text represents a simple molecule (water, HCl, etc.)
 *
 * Simple molecules have no "Core:" line and no newlines - just a name.
 */
function isSimpleMolecule(altText: string): boolean {
  return !altText.includes('\n') && !altText.startsWith('Core:');
}

/**
 * Build a WorkspaceFeatureResult for a single molecule (connected component)
 * by filtering the full workspace result to only include features belonging
 * to atoms in this molecule.
 */
function buildMoleculeFeatureResult(
  fullResult: WorkspaceFeatureResult,
  moleculeAtomIds: Set<string>
): WorkspaceFeatureResult {
  // Filter features to only those with atoms in this molecule
  const moleculeFeatures = fullResult.features.filter(wf =>
    wf.atomIds.some(atomId => moleculeAtomIds.has(atomId))
  );

  return {
    features: moleculeFeatures,
    graph: fullResult.graph,
    indexToAtomId: fullResult.indexToAtomId,
  };
}

/**
 * Package workspace output for the Piph Author LLM.
 *
 * Handles workspaces with one or more molecules, formatting each according
 * to the PIPH-89 spec:
 *
 * - Single molecule: No "Molecule N:" label
 * - Multiple molecules: Labeled and sorted by atom count (largest first)
 * - Simple molecules: Inline format "Molecule N: {name}"
 *
 * @param workspace - The molecular workspace
 * @param featureResult - Output from extractWorkspaceFeatures()
 * @param options - Optional configuration (add-ons reserved for future)
 * @returns Formatted string for LLM prompt
 *
 * @example Single molecule
 * ```
 * Workspace:
 *
 * Core: 6-membered ring
 *
 * 6-membered ring:
 * - Alcohol at carbon-2
 * ```
 *
 * @example Multiple molecules
 * ```
 * Workspace: 2 molecules
 *
 * Molecule 1:
 * Core: 6-membered ring
 *
 * 6-membered ring:
 * - Alcohol at carbon-2
 *
 * Molecule 2: water
 * ```
 */
export function buildWorkspacePackage(
  spec: WorkspaceSpec,
  _options?: WorkspacePackageOptions
): string {
  if (spec.type === 'rawText') {
    return `Workspace:\n${spec.text}`;
  }

  const { workspace, featureResult } = spec;

  // Find connected components (molecules)
  const components = findConnectedComponents(workspace);

  // Handle empty workspace
  if (components.length === 0) {
    return 'Workspace:\n\nEmpty workspace';
  }

  // PIPH-109: Sort components by atom count BEFORE processing
  // This ensures workspace summary and fact block use the same molecule ordering
  const sortedComponents = [...components].sort((a, b) => {
    const atomDiff = b.atomIds.length - a.atomIds.length;
    if (atomDiff !== 0) return atomDiff;
    // For ties, preserve original discovery order
    return components.indexOf(a) - components.indexOf(b);
  });

  // Process each molecule (in sorted order)
  const molecules: MoleculeData[] = sortedComponents.map((component, index) => {
    const moleculeAtomIds = new Set(component.atomIds);

    // Build feature result for this molecule
    const moleculeResult = buildMoleculeFeatureResult(featureResult, moleculeAtomIds);

    // PIPH-140: Get all atom symbols and net charge for simple molecules
    const allAtomSymbols = component.atomIds.map(atomId => {
      const atom = workspace.getAtom(atomId);
      return atom?.symbol ?? '';
    }).filter(Boolean);

    const netCharge = component.atomIds.reduce(
      (sum, id) => sum + (workspace.getAtom(id)?.charge ?? 0), 0
    );

    // Generate alt text for this molecule. `buildStructuredAltText` builds
    // its own per-molecule AtomLabelResolver from `moleculeResult` — labels
    // come from each feature's `atomLabels` array.
    const altText = buildStructuredAltText(moleculeResult, allAtomSymbols, netCharge);

    return {
      atomCount: component.atomIds.length,
      altText,
      isSimple: isSimpleMolecule(altText),
      originalIndex: index,
    };
  });

  // PIPH-87: Build workspace summary with sorted components
  const workspaceSummary = buildWorkspaceSummary(workspace, featureResult, sortedComponents);

  // Format output
  const output: string[] = [];

  if (molecules.length === 1) {
    // Single molecule - summary on its own line
    output.push('Workspace:');
    output.push(workspaceSummary);
    output.push('');
    output.push(molecules[0].altText);
  } else {
    // Multiple molecules - summary in header
    output.push(`Workspace: ${workspaceSummary}`);
    output.push('');

    for (let i = 0; i < molecules.length; i++) {
      const mol = molecules[i];
      const label = `Molecule ${i + 1}`;

      if (mol.isSimple) {
        // Simple molecule: inline format
        output.push(`${label}: ${mol.altText}`);
      } else {
        // Complex molecule: block format
        output.push(`${label}:`);
        output.push(mol.altText);
      }
      output.push('');
    }
  }

  // Note: add-ons are stubbed for future implementation
  // if (options?.addOns) { ... }

  // PIPH-97: Append stereo spatial context if requested
  if (_options?.stereoSpatial) {
    const stereoBlock = buildStereoSpatialBlock(workspace, featureResult);
    if (stereoBlock) {
      output.push('');
      output.push(stereoBlock);
    }
  }

  // PIPH-98: Append curved arrow context if requested
  if (_options?.curvedArrows) {
    const arrowBlock = buildCurvedArrowBlock(workspace, featureResult);
    if (arrowBlock) {
      output.push('');
      output.push(arrowBlock);
    }
  }

  return output.join('\n').trimEnd();
}
